import { useEffect, useMemo, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import { AircraftType, Airstrip, AirstripDistance, Customer, AdminSettings, Quote, Role, Leg, QuoteLegExtraServiceSelection, ResolvedExtraService } from '../types';
import { evaluateCustomRules, filterAdvisories, getCharterAdvisories, getEnforcementPolicy } from '../services/advisoryRules';
import { agentService } from '../services/agent';
import { ratesService } from '../services/rates';
import { fetchAirstripWeather } from '../services/weather';
import { quoteApi } from '../services/quoteApi';
import { extraServicesApi, toCostLineFromSelection } from '../services/extraServices';
import { resolveRouteMatrixMetrics, routeMatrixKtsToMph } from '../utils/routeMatrix';

type UseQuoteViewArgs = {
  aircrafts: AircraftType[];
  airstrips: Airstrip[];
  customers: Customer[];
  settings: AdminSettings;
  quotes: Quote[];
  setQuotes: Dispatch<SetStateAction<Quote[]>>;
  currentRole: Role;
  currentUser?: { charterDiscountCap?: number } | null;
  onLog?: (module: any, action: string, details: string, severity: string) => void;
};

export const useQuoteView = ({
  aircrafts,
  airstrips,
  customers,
  settings,
  quotes,
  setQuotes,
  currentRole,
  currentUser,
  onLog,
}: UseQuoteViewArgs) => {
  const [isCreating, setIsCreating] = useState(false);
  const [discountSource, setDiscountSource] = useState<'manual' | 'customer' | 'group' | null>(null);
  const [editingQuoteId, setEditingQuoteId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    customerId: customers[0]?.id || '',
    aircraftModel: aircrafts[0]?.name || '',
    date: new Date().toISOString().split('T')[0],
    issuerDiscount: 0,
    ceoDiscount: 0,
    pricingMode: 'HOURS' as 'HOURS' | 'MILEAGE',
    waitingMinutes: 0,
    sleepOverEnabled: false,
    sleepOverOption: 'NONE' as 'NONE' | 'TIER1' | 'TIER2' | 'CUSTOM',
    sleepOverCustomAmount: 0,
    bookingId: '',
    pnrCode: '',
    legs: [] as Leg[],
  });
  const [sectorCommercialsByLeg, setSectorCommercialsByLeg] = useState<Record<string, any>>({});
  const [weatherByCode, setWeatherByCode] = useState<Record<string, any>>({});
  const [airstripDistances, setAirstripDistances] = useState<AirstripDistance[]>([]);
  const [resolvedExtraServicesByLeg, setResolvedExtraServicesByLeg] = useState<Record<string, ResolvedExtraService[]>>({});
  const weatherTimersRef = useRef<Record<string, any>>({});
  const charterDiscountLimit = Number(currentUser?.charterDiscountCap ?? settings.discountCapsByRole[currentRole] ?? 0);
  const selectedAircraft = useMemo(
    () => aircrafts.find(a => a.name === formData.aircraftModel) || aircrafts[0],
    [aircrafts, formData.aircraftModel]
  );
  const canonicalizeAirstripCode = (value?: string | null) => {
    const code = String(value || '').trim().toUpperCase();
    if (!code) return '';
    if (code === 'ARK' || code === 'HTAR' || code === 'ARUSHA') return 'HTAR';
    return code;
  };
  const findAirstripByAnyCode = (value?: string | null) => {
    const normalized = canonicalizeAirstripCode(value);
    if (!normalized) return null;
    return (
      airstrips.find((a) => canonicalizeAirstripCode(a.code || '') === normalized) ||
      airstrips.find((a) => canonicalizeAirstripCode(a.icao || '') === normalized) ||
      null
    );
  };
  const toLookupAirstripCode = (value?: string | null) => {
    const matched = findAirstripByAnyCode(value);
    return String(matched?.code || canonicalizeAirstripCode(value) || '').toUpperCase();
  };
  const getRouteDefaults = () => settings.routeDefaults || {
    cruiseSpeedKts: 120,
    ratePerNmUSD: 0,
    ratePerHourUSD: 0,
    distanceComputationModel: 'GEODESIC',
    distanceBufferPercent: 0,
    distanceBaseMiles: 0,
    roundDistanceUp: false,
    blockTimeMultiplier: 1,
    blockTimeBaseMinutes: 0,
    blockTimeRoundingMinutes: 1,
  };
  const KTS_TO_MPH = routeMatrixKtsToMph;
  const BUSINESS_TIME_ZONE = 'Africa/Dar_es_Salaam';
  const milesPlanar = (lat1: number, lon1: number, lat2: number, lon2: number) => {
    const deltaLat = Number(lat2 || 0) - Number(lat1 || 0);
    const deltaLon = Number(lon2 || 0) - Number(lon1 || 0);
    const meanLatRad = ((Number(lat1 || 0) + Number(lat2 || 0)) / 2) * (Math.PI / 180);
    const milesPerDegreeLat = 69.0;
    const milesPerDegreeLon = 69.172 * Math.cos(meanLatRad);
    const northSouthMiles = deltaLat * milesPerDegreeLat;
    const eastWestMiles = deltaLon * milesPerDegreeLon;
    return Math.sqrt((northSouthMiles ** 2) + (eastWestMiles ** 2));
  };
  const getDistanceOverride = (fromCode?: string | null, toCode?: string | null) => {
    const from = toLookupAirstripCode(fromCode);
    const to = toLookupAirstripCode(toCode);
    if (!from || !to) return null;
    return airstripDistances.find(item => item.fromCode === from && item.toCode === to) || null;
  };
  const getDerivedLegMetrics = (
    leg: Partial<Leg>,
    options?: {
      preferManualDistance?: boolean;
      preferManualBlock?: boolean;
      deadLegFallback?: number;
    }
  ) => {
    const from = findAirstripByAnyCode(leg.from);
    const to = findAirstripByAnyCode(leg.to);
    if (!from || !to) return null;
    const routeDefaults = getRouteDefaults();
    const override = getDistanceOverride(from.code, to.code);
    const matrixMetrics = resolveRouteMatrixMetrics({ from, to, routeDefaults, override });
    const distanceMiles = matrixMetrics.distanceMiles;
    let blockMinutes = matrixMetrics.blockMinutes;
    if (blockMinutes <= 0 && leg.isDeadLeg) {
      blockMinutes = Math.max(1, Number(options?.deadLegFallback || leg.blockTimeMinutes || 60));
    }
    return {
      distanceMiles,
      blockMinutes,
    };
  };
  const addMinutesToIso = (iso: string, minutes: number) => {
    const base = new Date(iso);
    if (Number.isNaN(base.getTime())) return '';
    return new Date(base.getTime() + minutes * 60000).toISOString();
  };
  const getBusinessDateTimeParts = (value?: string | null) => {
    if (!value) return null;
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return null;
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: BUSINESS_TIME_ZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    const parts = formatter.formatToParts(parsed);
    const part = (type: string) => parts.find((entry) => entry.type === type)?.value || '';
    const year = part('year');
    const month = part('month');
    const day = part('day');
    const hour = Number(part('hour') || 0);
    const minute = Number(part('minute') || 0);
    if (!year || !month || !day) return null;
    return {
      dateKey: `${year}-${month}-${day}`,
      hour,
      minute,
      timeLabel: `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`,
    };
  };
  const normalizeIso = (value?: string | null, fallbackDate?: string) => {
    if (!value) return '';
    if (value.includes('T')) {
      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime()) ? '' : parsed.toISOString();
    }
    if (/^\d{2}:\d{2}$/.test(value)) {
      const dateStr = fallbackDate || formData.date || new Date().toISOString().slice(0, 10);
      const parsed = new Date(`${dateStr}T${value}`);
      return Number.isNaN(parsed.getTime()) ? '' : parsed.toISOString();
    }
    return '';
  };
  const getLegStartIso = (leg: Partial<Leg>) =>
    normalizeIso(leg.etd, getMissionDateKey(leg)) ||
    `${getMissionDateKey(leg)}T08:00:00.000Z`;
  const getLegEndIso = (leg: Partial<Leg>, blockMinutes: number) => {
    const etaIso = normalizeIso(leg.eta, getMissionDateKey(leg));
    if (etaIso && new Date(etaIso).getTime() > new Date(getLegStartIso(leg)).getTime()) return etaIso;
    return addMinutesToIso(getLegStartIso(leg), Math.max(1, blockMinutes));
  };
  const selectedAircraftId = selectedAircraft?.id || '';
  const getBaseAirstrip = () => canonicalizeAirstripCode(settings.returnBaseAirstrip) || 'HTAR';
  const isSystemGeneratedLeg = (leg?: Partial<Leg> | null) => {
    const id = String(leg?.id || '').toUpperCase();
    return (
      id.startsWith('AUTO-PRE-') ||
      id.startsWith('AUTO-POST-') ||
      id.startsWith('AUTO-IMM-') ||
      id.includes('-AUTO-IMM-')
    );
  };
  const isImmigrationGeneratedLeg = (leg?: Partial<Leg> | null) => {
    const id = String(leg?.id || '').toUpperCase();
    return id.startsWith('AUTO-IMM-') || id.includes('-AUTO-IMM-');
  };
  const normalizeCountry = (value?: string | null) => String(value || '').trim().toUpperCase();
  const getMissionDateKey = (leg?: Partial<Leg> | null) => {
    const candidates = [leg?.etd, leg?.eta];
    for (const value of candidates) {
      if (!value) continue;
      if (typeof value === 'string') {
        if (/^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);
        const parsed = new Date(value);
        if (!Number.isNaN(parsed.getTime())) return parsed.toISOString().slice(0, 10);
      }
    }
    return formData.date || new Date().toISOString().slice(0, 10);
  };
  const getNearestImmigrationCode = (
    referenceCode: string,
    country?: string | null,
    excludeCodes: string[] = [],
  ) => {
    const reference = findAirstripByAnyCode(referenceCode);
    if (!reference) return '';
    const normalizedCountry = normalizeCountry(country || reference.country);
    const excluded = new Set(
      excludeCodes
        .map(item => canonicalizeAirstripCode(item))
        .filter(Boolean)
    );
    const candidates = airstrips.filter((candidate) => {
      if (!candidate?.isBorderPost) return false;
      const candidateCode = canonicalizeAirstripCode(candidate.code || candidate.icao || '');
      if (!candidateCode) return false;
      if (excluded.has(candidateCode)) return false;
      const candidateCountry = normalizeCountry(candidate.country);
      return normalizedCountry ? candidateCountry === normalizedCountry : true;
    });
    if (!candidates.length) return '';
    let bestCode = '';
    let bestMiles = Number.POSITIVE_INFINITY;
    candidates.forEach((candidate) => {
      const miles = milesPlanar(
        reference.latitude,
        reference.longitude,
        candidate.latitude,
        candidate.longitude,
      );
      if (miles < bestMiles) {
        bestMiles = miles;
        bestCode = canonicalizeAirstripCode(candidate.code || candidate.icao || '');
      }
    });
    return bestCode;
  };
  const getNextOperationalMorningIso = (value?: string | null) => {
    const parsed = value ? new Date(value) : new Date();
    const safe = Number.isNaN(parsed.getTime()) ? new Date() : parsed;
    const next = new Date(safe);
    next.setDate(next.getDate() + 1);
    next.setHours(6, 30, 0, 0);
    return next.toISOString();
  };
  const normalizeCharterLegsWithHomeBase = (legs: Leg[], options?: { sleepOverActive?: boolean }) => {
    const source = Array.isArray(legs) ? legs : [];
    if (!source.length) return [] as Leg[];
    const userLegs = source.filter((leg) => !isSystemGeneratedLeg(leg));
    if (!userLegs.length) return [] as Leg[];
    const baseAirstrip = getBaseAirstrip();
    const sleepOverActive = Boolean(options?.sleepOverActive);
    const stamped = userLegs.map((leg, index) => ({
      ...leg,
      from: canonicalizeAirstripCode(leg.from),
      to: canonicalizeAirstripCode(leg.to),
      _missionDateKey: getMissionDateKey(leg),
      _sourceIndex: index,
    })) as Array<Leg & { _sourceIndex?: number; _missionDateKey?: string }>;
    const missionGroups: Array<Array<Leg & { _sourceIndex?: number; _missionDateKey?: string }>> = [];
    stamped.forEach((leg) => {
      const currentGroup = missionGroups[missionGroups.length - 1];
      if (!currentGroup || currentGroup[0]?._missionDateKey !== leg._missionDateKey) {
        missionGroups.push([leg]);
        return;
      }
      currentGroup.push(leg);
    });

    const normalizeMissionLegs = (missionLegs: Array<Leg & { _sourceIndex?: number; _missionDateKey?: string }>) => {
      const missionNormalized: Array<Leg & { _sourceIndex?: number; _missionDateKey?: string }> = [];
      missionLegs.forEach((leg) => {
        const derivedMetrics = getDerivedLegMetrics(leg, { preferManualDistance: true, preferManualBlock: true, deadLegFallback: 60 });
        const legBlockMinutes = Math.max(1, Number(derivedMetrics?.blockMinutes || leg.blockTimeMinutes || 60));
        const legStartIso = getLegStartIso(leg);
        const legEndIso = getLegEndIso(leg, legBlockMinutes);
        const fromAirstrip = findAirstripByAnyCode(leg.from);
        const toAirstrip = findAirstripByAnyCode(leg.to);
        const fromCountry = normalizeCountry(fromAirstrip?.country);
        const toCountry = normalizeCountry(toAirstrip?.country);
        const isInternational = Boolean(fromCountry && toCountry && fromCountry !== toCountry);
        if (!isInternational) {
          missionNormalized.push({
            ...leg,
            blockTimeMinutes: legBlockMinutes,
            etd: legStartIso,
            eta: legEndIso,
          });
          return;
        }
        const routeCodes: string[] = [canonicalizeAirstripCode(leg.from)];
        const fromIsImmigration = Boolean(fromAirstrip?.isBorderPost);
        const toIsImmigration = Boolean(toAirstrip?.isBorderPost);
        if (!fromIsImmigration) {
          const exitCode = getNearestImmigrationCode(leg.from, fromCountry, [leg.from, leg.to]);
          if (exitCode && exitCode !== canonicalizeAirstripCode(leg.from)) routeCodes.push(exitCode);
        }
        if (!toIsImmigration) {
          const entryCode = getNearestImmigrationCode(leg.to, toCountry, [leg.from, leg.to, routeCodes[routeCodes.length - 1]]);
          if (entryCode && entryCode !== routeCodes[routeCodes.length - 1]) routeCodes.push(entryCode);
        }
        routeCodes.push(canonicalizeAirstripCode(leg.to));
        const compactCodes = routeCodes.filter((code, idx, arr) => !!code && (idx === 0 || code !== arr[idx - 1]));
        if (compactCodes.length <= 2) {
          missionNormalized.push({
            ...leg,
            blockTimeMinutes: legBlockMinutes,
            etd: legStartIso,
            eta: legEndIso,
          });
          return;
        }
        const fallbackStart = new Date(legStartIso || `${formData.date}T08:00`);
        const safeStart = Number.isNaN(fallbackStart.getTime()) ? new Date(`${formData.date}T08:00`) : fallbackStart;
        const fallbackEnd = new Date(legEndIso || legStartIso || `${formData.date}T09:00`);
        const safeEnd = Number.isNaN(fallbackEnd.getTime()) || fallbackEnd.getTime() <= safeStart.getTime()
          ? new Date(safeStart.getTime() + legBlockMinutes * 60000)
          : fallbackEnd;
        const totalMinutes = Math.max(1, Math.round((safeEnd.getTime() - safeStart.getTime()) / 60000));
        const segmentCount = compactCodes.length - 1;
        const baseSegmentMinutes = Math.max(1, Math.floor(totalMinutes / segmentCount));
        let cursor = safeStart.getTime();

        for (let idx = 0; idx < segmentCount; idx += 1) {
          const isLast = idx === segmentCount - 1;
          const segmentFrom = compactCodes[idx];
          const segmentTo = compactCodes[idx + 1];
          const nextCursor = isLast
            ? safeEnd.getTime()
            : cursor + (baseSegmentMinutes * 60000);
          const segmentMinutes = Math.max(1, Math.round((nextCursor - cursor) / 60000));
          const segmentId = `${leg.id || `LEG-${leg._sourceIndex || 0}`}-AUTO-IMM-${idx + 1}`;
          missionNormalized.push({
            ...leg,
            id: segmentId,
            from: segmentFrom,
            to: segmentTo,
            isDeadLeg: leg.isDeadLeg || false,
            distance: 0,
            blockTimeMinutes: segmentMinutes,
            etd: new Date(cursor).toISOString(),
            eta: new Date(nextCursor).toISOString(),
            meetAndAssistActive: isLast ? leg.meetAndAssistActive : false,
            loungeActive: isLast ? leg.loungeActive : false,
            extraServiceSelections: isLast ? (leg.extraServiceSelections || []) : [],
          });
          cursor = nextCursor;
        }
      });

      return missionNormalized;
    };

    const normalized: Array<Leg & { _sourceIndex?: number; _missionDateKey?: string }> = [];
    let carryForwardOrigin = baseAirstrip;
    missionGroups.forEach((missionLegs, missionIndex) => {
      const missionExpanded = normalizeMissionLegs(missionLegs);
      const first = missionExpanded[0];
      const last = missionExpanded[missionExpanded.length - 1];
      if (!first || !last) return;
      const nextMission = missionGroups[missionIndex + 1];
      const missionStartOrigin = sleepOverActive ? carryForwardOrigin : baseAirstrip;

      if (first.from && first.from !== missionStartOrigin) {
        const repositionMetrics = getDerivedLegMetrics({
          from: missionStartOrigin,
          to: first.from,
          isDeadLeg: true,
        }, { preferManualDistance: false, preferManualBlock: false, deadLegFallback: 60 });
        const repositionMinutes = Math.max(1, Number(repositionMetrics?.blockMinutes || 60));
        const firstEtd = new Date(getLegStartIso(first) || `${formData.date}T08:00`);
        const safeFirstEtd = Number.isNaN(firstEtd.getTime()) ? new Date(`${formData.date}T08:00`) : firstEtd;
        const repositionEta = new Date(safeFirstEtd);
        const repositionEtd = new Date(repositionEta.getTime() - repositionMinutes * 60000);
        normalized.push({
          id: `AUTO-PRE-${first.id || 'LEG1'}`,
          from: missionStartOrigin,
          to: first.from,
          paxPick: 0,
          paxOB: 0,
          paxDrop: 0,
          paxCount: 0,
          isDeadLeg: true,
          distance: 0,
          blockTimeMinutes: repositionMinutes,
          etd: repositionEtd.toISOString(),
          eta: repositionEta.toISOString(),
          tacticalStatus: 'OPEN',
          meetAndAssistActive: false,
          meetAndAssistScope: 'DESTINATION',
          loungeActive: false,
          loungeScope: 'DESTINATION',
          extraServiceSelections: [],
          _missionDateKey: first._missionDateKey,
        });
      }

      normalized.push(...missionExpanded);

      if (sleepOverActive && nextMission?.length) {
        carryForwardOrigin = canonicalizeAirstripCode(last.to) || baseAirstrip;
        return;
      }

      if (last.to && last.to !== baseAirstrip) {
        const repositionMetrics = getDerivedLegMetrics({
          from: last.to,
          to: baseAirstrip,
          isDeadLeg: true,
        }, { preferManualDistance: false, preferManualBlock: false, deadLegFallback: 60 });
        const repositionMinutes = Math.max(1, Number(repositionMetrics?.blockMinutes || 60));
        const lastEta = new Date(getLegEndIso(last, Number(last.blockTimeMinutes || repositionMinutes)) || `${formData.date}T09:00`);
        const safeLastEta = Number.isNaN(lastEta.getTime()) ? new Date(`${formData.date}T09:00`) : lastEta;
        const repositionEtd = sleepOverActive
          ? new Date(getNextOperationalMorningIso(safeLastEta.toISOString()))
          : new Date(safeLastEta.getTime() + 30 * 60000);
        const repositionEta = new Date(repositionEtd.getTime() + repositionMinutes * 60000);
        normalized.push({
          id: `AUTO-POST-${last.id || 'LEGN'}`,
          from: last.to,
          to: baseAirstrip,
          paxPick: 0,
          paxOB: 0,
          paxDrop: 0,
          paxCount: 0,
          isDeadLeg: true,
          distance: 0,
          blockTimeMinutes: repositionMinutes,
          etd: repositionEtd.toISOString(),
          eta: repositionEta.toISOString(),
          tacticalStatus: 'OPEN',
          meetAndAssistActive: false,
          meetAndAssistScope: 'DESTINATION',
          loungeActive: false,
          loungeScope: 'DESTINATION',
          extraServiceSelections: [],
          _missionDateKey: last._missionDateKey,
        });
      }
      carryForwardOrigin = baseAirstrip;
    });

    return normalized as Leg[];
  };
  const hasValidReturnToBase = (legs: Leg[]) => {
    if (!Array.isArray(legs) || legs.length === 0) return false;
    const lastLeg = legs[legs.length - 1];
    return canonicalizeAirstripCode(lastLeg?.to || '') === canonicalizeAirstripCode(getBaseAirstrip());
  };

  useEffect(() => {
    if (!isCreating) return;
    try {
      const context = {
        customerId: formData.customerId,
        aircraftModel: formData.aircraftModel,
        date: formData.date,
        discount: formData.issuerDiscount + formData.ceoDiscount,
        legs: formData.legs,
        totals: { legsCount: Array.isArray(formData.legs) ? formData.legs.length : 0 },
      };
      localStorage.setItem('ma_agent_context_QUOTER', JSON.stringify(context));
    } catch {
      // ignore storage errors
    }
  }, [formData, isCreating]);

  const handleIssuerDiscountChange = (newDiscount: number) => {
    const safeDiscount = Math.max(0, Math.min(charterDiscountLimit, Number(newDiscount) || 0));
    setFormData(prev => ({ ...prev, issuerDiscount: safeDiscount }));
    setDiscountSource('manual');
  };

  const handleCeoDiscountChange = (newDiscount: number) => {
    setFormData(prev => ({ ...prev, ceoDiscount: newDiscount }));
    setDiscountSource('manual');
  };

  const resetDiscountSource = () => {
    setDiscountSource(null);
  };

  const startNewQuote = () => {
    const arushaDefault = airstrips.find(a =>
      String(a.name || '').toUpperCase().includes('ARUSHA') ||
      String(a.code || '').toUpperCase() === 'ARK' ||
      String(a.code || '').toUpperCase() === 'HTAR' ||
      String(a.icao || '').toUpperCase() === 'HTAR'
    )?.code;
    const defaultFrom = canonicalizeAirstripCode(arushaDefault || settings.returnBaseAirstrip || airstrips[0]?.code || '') || 'HTAR';
    setFormData({
      customerId: customers[0]?.id || '',
      aircraftModel: aircrafts[0]?.name || '',
      date: new Date().toISOString().split('T')[0],
      issuerDiscount: 0,
      ceoDiscount: 0,
      pricingMode: 'HOURS',
      waitingMinutes: 0,
      sleepOverEnabled: false,
      sleepOverOption: 'NONE',
      sleepOverCustomAmount: 0,
      legs: [{
        id: `L-${Date.now()}`,
        from: defaultFrom,
        to: '',
        paxPick: 2,
        paxOB: 2,
        paxDrop: 0,
        paxCount: 2,
        isDeadLeg: false,
        distance: 0,
        blockTimeMinutes: 0,
        etd: new Date(`${new Date().toISOString().split('T')[0]}T08:00`).toISOString(),
        eta: new Date(`${new Date().toISOString().split('T')[0]}T08:55`).toISOString(),
        tacticalStatus: 'OPEN',
        meetAndAssistActive: false,
        meetAndAssistScope: 'DESTINATION',
        loungeActive: false,
        loungeScope: 'DESTINATION',
        extraServiceSelections: [],
      }],
    });
    setDiscountSource(null);
    setEditingQuoteId(null);
    setIsCreating(true);
  };

  const toDateInput = (value?: string) => {
    if (!value) return new Date().toISOString().split('T')[0];
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return new Date().toISOString().split('T')[0];
    return parsed.toISOString().split('T')[0];
  };

  const toIsoFromMaybe = (value?: string, dateFallback?: string) => {
    if (!value) return '';
    if (value.includes('T')) return value;
    if (/^\d{2}:\d{2}$/.test(value)) {
      const dateStr = dateFallback || new Date().toISOString().split('T')[0];
      const parsed = new Date(`${dateStr}T${value}`);
      if (Number.isNaN(parsed.getTime())) return '';
      return parsed.toISOString();
    }
    return '';
  };

  const selectQuote = (quote: Quote) => {
    const quoteDate = toDateInput(quote.date);
    const inferBlockMinutes = (etdValue?: string, etaValue?: string, fallback = 0) => {
      const etdIso = toIsoFromMaybe(etdValue, quoteDate);
      const etaIso = toIsoFromMaybe(etaValue, quoteDate);
      if (etdIso && etaIso) {
        const etdMs = new Date(etdIso).getTime();
        const etaMs = new Date(etaIso).getTime();
        if (Number.isFinite(etdMs) && Number.isFinite(etaMs) && etaMs > etdMs) {
          return Math.max(0, Math.round((etaMs - etdMs) / 60000));
        }
      }
      return Math.max(0, fallback);
    };
    const legs = (quote.legs || []).map((leg, index) => ({
      id: leg.id || `L-${Date.now()}-${index}`,
      from: canonicalizeAirstripCode(leg.from || leg.departureICAO || ''),
      to: canonicalizeAirstripCode(leg.to || leg.arrivalICAO || ''),
      paxPick: leg.paxCount || 0,
      paxOB: leg.paxCount || 0,
      paxDrop: 0,
      paxCount: leg.paxCount || 0,
      isDeadLeg: false,
      distance: leg.distance || 0,
      blockTimeMinutes: inferBlockMinutes((leg as any).etd, (leg as any).eta, leg.blockTimeMinutes || 0),
      etd: toIsoFromMaybe((leg as any).etd, quoteDate),
      eta: toIsoFromMaybe((leg as any).eta, quoteDate),
      tacticalStatus: leg.tacticalStatus || 'OPEN',
      meetAndAssistActive: (leg as any).meetAndAssistActive || false,
      meetAndAssistScope: (leg as any).meetAndAssistScope || 'DESTINATION',
      loungeActive: (leg as any).loungeActive || false,
      loungeScope: (leg as any).loungeScope || 'DESTINATION',
      extraServiceSelections: (leg as any).extraServiceSelections || [],
    }));

    const customer = customers.find(c => c.id === quote.customerId);
    const groupDiscount = customer?.categoryAssignments?.[0]?.Category?.defaultCharterDiscount || 0;
    const customerDiscount = customer?.charterDiscount || 0;
    const clientDiscountPercent = customerDiscount > 0 ? customerDiscount : groupDiscount;
    const discountBreakdown = (quote as any)?.costs?.discountBreakdown || {};
    const totalDiscountPercent = quote.discountPercentage || 0;
    const savedSleepOverOption = String((quote as any)?.costs?.sleepOverOption || 'NONE').toUpperCase();
    const savedPricingMode = String((quote as any)?.costs?.pricingMode || '').toUpperCase();
    const sleepOverOption: 'NONE' | 'TIER1' | 'TIER2' | 'CUSTOM' =
      savedSleepOverOption === 'TIER1' || savedSleepOverOption === 'TIER2' || savedSleepOverOption === 'CUSTOM'
        ? (savedSleepOverOption as 'TIER1' | 'TIER2' | 'CUSTOM')
        : 'NONE';
    const pricingMode: 'HOURS' | 'MILEAGE' = savedPricingMode === 'MILEAGE' ? 'MILEAGE' : 'HOURS';
    const sleepOverCustomAmount = Math.max(0, Number((quote as any)?.costs?.sleepOverCustomAmount || 0));
    const issuerDiscount =
      discountBreakdown.issuerPercent !== undefined
        ? Number(discountBreakdown.issuerPercent) || 0
        : Math.max(0, totalDiscountPercent - clientDiscountPercent);
    const ceoDiscount = discountBreakdown.ceoPercent !== undefined ? Number(discountBreakdown.ceoPercent) || 0 : 0;

    setFormData({
      customerId: quote.customerId,
      aircraftModel: quote.aircraftModel || selectedAircraft?.name || '',
      date: quoteDate,
      issuerDiscount,
      ceoDiscount,
      pricingMode,
      waitingMinutes: 0,
      sleepOverEnabled: sleepOverOption !== 'NONE',
      sleepOverOption,
      sleepOverCustomAmount,
      bookingId: quote.bookingId,
      pnrCode: quote.pnrCode,
      legs: legs.length > 0 ? legs : [],
    });
    setDiscountSource(null);
    setEditingQuoteId(quote.id);
    setIsCreating(true);
  };

  const addLeg = () => {
    const legs = normalizeCharterLegsWithHomeBase(Array.isArray(formData.legs) ? formData.legs : [], { sleepOverActive: operationalSleepOverActive });
    const lastLeg = legs.length > 0 ? legs[legs.length - 1] : null;
    const fallbackFrom = canonicalizeAirstripCode(settings.returnBaseAirstrip || airstrips[0]?.code || '') || 'HTAR';
    const baseDate = formData.date || new Date().toISOString().split('T')[0];
    const defaultEtd = new Date(`${baseDate}T10:00`).toISOString();
    const newLeg: Leg = {
      id: `L-${Date.now()}`,
      from: canonicalizeAirstripCode(lastLeg?.to || fallbackFrom),
      to: '',
      paxPick: 0,
      paxOB: lastLeg?.paxOB || 0,
      paxDrop: 0,
      paxCount: lastLeg?.paxOB || 0,
      isDeadLeg: false,
      distance: 0,
      blockTimeMinutes: 0,
      etd: defaultEtd,
      eta: new Date(new Date(defaultEtd).getTime() + 45 * 60000).toISOString(),
      tacticalStatus: 'OPEN',
      meetAndAssistScope: 'DESTINATION',
      loungeScope: 'DESTINATION',
      extraServiceSelections: [],
    };
    setFormData(prev => ({ ...prev, legs: [...(Array.isArray(prev.legs) ? prev.legs : []), newLeg] }));
  };

  const autoReturnToBase = () => {
    const legs = normalizeCharterLegsWithHomeBase(Array.isArray(formData.legs) ? formData.legs : [], { sleepOverActive: operationalSleepOverActive });
    const lastLeg = legs.length > 0 ? legs[legs.length - 1] : null;
    if (!lastLeg) return;
    const isValidAirstrip = (code?: string | null) => {
      const normalized = canonicalizeAirstripCode(code);
      return !!normalized && airstrips.some(a => canonicalizeAirstripCode(a.code || a.icao || '') === normalized);
    };
    const baseCandidates = [
      canonicalizeAirstripCode(settings.returnBaseAirstrip),
      canonicalizeAirstripCode(legs[0]?.from),
      canonicalizeAirstripCode(lastLeg.from),
    ].filter(Boolean) as string[];
    const baseAirstrip = baseCandidates.find(code => isValidAirstrip(code));
    if (!baseAirstrip || canonicalizeAirstripCode(lastLeg.to) === canonicalizeAirstripCode(baseAirstrip)) return;

    const baseDate = formData.date || new Date().toISOString().split('T')[0];
    const defaultEtd = new Date(`${baseDate}T12:00`).toISOString();
    const returnLeg: Leg = {
      id: `DL-${Date.now()}`,
      from: canonicalizeAirstripCode(lastLeg.to),
      to: canonicalizeAirstripCode(baseAirstrip),
      paxPick: 0,
      paxOB: 0,
      paxDrop: 0,
      paxCount: 0,
      isDeadLeg: true,
      distance: 0,
      blockTimeMinutes: 0,
      etd: defaultEtd,
      eta: new Date(new Date(defaultEtd).getTime() + 55 * 60000).toISOString(),
      tacticalStatus: 'OPEN',
    };
    setFormData(prev => ({ ...prev, legs: [...prev.legs, returnLeg] }));
  };

  const removeLeg = (id: string) => {
    setFormData(prev => ({
      ...prev,
      legs: Array.isArray(prev.legs) ? prev.legs.filter(l => l.id !== id) : [],
    }));
  };

  const updateLeg = (id: string, updates: Partial<Leg>) => {
    setFormData(prev => {
      const nextLegs = Array.isArray(prev.legs)
        ? prev.legs.map(l => {
            if (l.id !== id) return l;
            const candidate = { ...l, ...updates };
            const shouldRecomputeBlock = (updates.from !== undefined || updates.to !== undefined) && updates.blockTimeMinutes === undefined;
            const derivedMetrics = shouldRecomputeBlock
              ? getDerivedLegMetrics(candidate, { preferManualDistance: true, preferManualBlock: false, deadLegFallback: 60 })
              : null;
            const nextBlockMinutes = updates.blockTimeMinutes !== undefined
              ? Number(updates.blockTimeMinutes || 0)
              : shouldRecomputeBlock
                ? Number(derivedMetrics?.blockMinutes || candidate.blockTimeMinutes || 0)
                : Number(candidate.blockTimeMinutes || 0);
            const shouldRecomputeEta = updates.eta === undefined && (
              updates.etd !== undefined ||
              updates.blockTimeMinutes !== undefined ||
              shouldRecomputeBlock
            );
            const normalizedEtd = normalizeIso(String(candidate.etd || ''), getMissionDateKey(candidate)) || candidate.etd || '';
            const nextEta = shouldRecomputeEta && normalizedEtd && nextBlockMinutes > 0
              ? addMinutesToIso(normalizedEtd, nextBlockMinutes)
              : candidate.eta;
            return {
              ...candidate,
              etd: normalizedEtd || candidate.etd,
              blockTimeMinutes: nextBlockMinutes,
              eta: nextEta,
            };
          })
        : [];
      return { ...prev, legs: nextLegs };
    });
    // Fetch METAR for both origin and destination when leg is updated
    if (updates.from) requestMetar(updates.from);
    if (updates.to) requestMetar(updates.to);
  };

  const requestMetar = (codeRaw: string) => {
    const code = canonicalizeAirstripCode(codeRaw);
    if (code.length < 3) return;
    
    // Find airstrip to get ICAO code if available
    const airstrip = findAirstripByAnyCode(code);
    const icaoCode = airstrip?.icao;
    
    // Use ICAO code if available, otherwise use airstrip code
    const weatherCode = icaoCode || code;
    
    if (weatherTimersRef.current[code]) {
      clearTimeout(weatherTimersRef.current[code]);
    }
    weatherTimersRef.current[code] = setTimeout(async () => {
      try {
        const data = await fetchAirstripWeather(code, icaoCode);
        setWeatherByCode(prev => ({ ...prev, [code]: data }));
      } catch {
        // ignore weather fetch errors
      }
    }, 350);
  };

  useEffect(() => {
    if (!isCreating) return;
    let cancelled = false;
    const loadDistances = async () => {
      try {
        const data = await ratesService.getAirstripDistances();
        if (!cancelled) setAirstripDistances(data);
      } catch {
        // ignore distance fetch errors
      }
    };
    loadDistances();
    return () => {
      cancelled = true;
    };
  }, [isCreating]);

  useEffect(() => {
    if (!isCreating) return;
    setFormData(prev => {
      const currentLegs = Array.isArray(prev.legs) ? prev.legs : [];
      let changed = false;
      const nextLegs = currentLegs.map((leg) => {
        if (isSystemGeneratedLeg(leg)) return leg;
        const derivedMetrics = getDerivedLegMetrics(leg, { preferManualDistance: true, preferManualBlock: false, deadLegFallback: 60 });
        if (!derivedMetrics?.blockMinutes) return leg;
        const normalizedEtd = normalizeIso(String(leg.etd || ''), getMissionDateKey(leg));
        const validEta = normalizeIso(String(leg.eta || ''), getMissionDateKey(leg));
        const shouldFillBlock = Number(leg.blockTimeMinutes || 0) <= 0;
        const shouldFillEta = !validEta && !!normalizedEtd;
        if (!shouldFillBlock && !shouldFillEta) return leg;
        changed = true;
        return {
          ...leg,
          blockTimeMinutes: shouldFillBlock ? derivedMetrics.blockMinutes : leg.blockTimeMinutes,
          eta: shouldFillEta ? addMinutesToIso(normalizedEtd, derivedMetrics.blockMinutes) : leg.eta,
        };
      });
      return changed ? { ...prev, legs: nextLegs } : prev;
    });
  }, [airstripDistances, settings, isCreating]);

  const sleepOverAssessment = useMemo(() => {
    const normalizedLegs = normalizeCharterLegsWithHomeBase(Array.isArray(formData.legs) ? formData.legs : [], { sleepOverActive: false });
    const missionMap = new Map<string, Leg[]>();
    normalizedLegs.forEach((leg) => {
      const key = getMissionDateKey(leg);
      const current = missionMap.get(key) || [];
      current.push(leg);
      missionMap.set(key, current);
    });
    const cutoffMinutes = (18 * 60) + 30;
    const triggeredMissions = Array.from(missionMap.entries()).flatMap(([missionDate, missionLegs]) => {
      const lastLeg = missionLegs[missionLegs.length - 1];
      if (!lastLeg || canonicalizeAirstripCode(lastLeg.to) !== getBaseAirstrip()) return [];
      const etaIso = getLegEndIso(lastLeg, Number(lastLeg.blockTimeMinutes || 0));
      const etaBusinessParts = getBusinessDateTimeParts(etaIso);
      const missionStartIso = getLegStartIso(missionLegs[0] || lastLeg);
      const missionBusinessParts = getBusinessDateTimeParts(missionStartIso);
      if (!etaBusinessParts || !missionBusinessParts) return [];
      const etaMinutes = (etaBusinessParts.hour * 60) + etaBusinessParts.minute;
      const qualifies = etaBusinessParts.dateKey > missionBusinessParts.dateKey || etaMinutes >= cutoffMinutes;
      if (!qualifies) return [];
      return [{
        missionDate: missionBusinessParts.dateKey || missionDate,
        etaIso,
        etaLabel: etaBusinessParts.timeLabel,
        etaDateKey: etaBusinessParts.dateKey,
      }];
    });

    const autoEligible = triggeredMissions.length > 0;
    const primary = triggeredMissions[0];
    const reason = primary
      ? `Auto unlock: return to ${getBaseAirstrip()} reaches ${primary.etaLabel} Tanzania time on ${primary.etaDateKey}, beyond the 18:30 cutoff.`
      : 'Disabled until you enable overnight manually, or the return to base reaches after 18:30 Tanzania time.';

    return {
      autoEligible,
      reason,
    };
  }, [formData.legs, formData.date, settings.returnBaseAirstrip, airstripDistances]);
  const operationalSleepOverActive = Boolean(formData.sleepOverEnabled || sleepOverAssessment.autoEligible);
  const sleepOverUnlocked = Boolean(formData.sleepOverEnabled || sleepOverAssessment.autoEligible);
  const previousSleepOverUnlockedRef = useRef(false);

  useEffect(() => {
    if (!isCreating) return;
    const legs = normalizeCharterLegsWithHomeBase(Array.isArray(formData.legs) ? formData.legs : [], { sleepOverActive: operationalSleepOverActive });
    let cancelled = false;
    const calculateAll = async () => {
      const results = await Promise.all(
        legs.map(async leg => {
          const data = await ratesService.calculateSectorCommercials(leg, settings);
          return { legId: leg.id, data };
        })
      );
      if (cancelled) return;
      const next: Record<string, any> = {};
      results.forEach(r => {
        next[r.legId] = r.data;
      });
      setSectorCommercialsByLeg(next);
    };
    calculateAll();
    legs.forEach(leg => {
      if (leg.from) requestMetar(leg.from);
      if (leg.to) requestMetar(leg.to);
    });
    return () => {
      cancelled = true;
    };
  }, [formData.legs, settings, isCreating, operationalSleepOverActive]);

  useEffect(() => {
    if (!isCreating) return;
    const legs = normalizeCharterLegsWithHomeBase(Array.isArray(formData.legs) ? formData.legs : [], { sleepOverActive: operationalSleepOverActive });
    if (!legs.length) {
      setResolvedExtraServicesByLeg({});
      return;
    }
    let cancelled = false;
    const resolveAll = async () => {
      const results = await Promise.all(
        legs.map(async leg => {
          try {
            const data = await extraServicesApi.resolveForLeg(leg, selectedAircraftId);
            return { legId: leg.id, data };
          } catch {
            return { legId: leg.id, data: [] as ResolvedExtraService[] };
          }
        })
      );
      if (cancelled) return;
      const next: Record<string, ResolvedExtraService[]> = {};
      results.forEach(item => {
        next[item.legId] = item.data;
      });
      setResolvedExtraServicesByLeg(next);
    };
    resolveAll();
    return () => {
      cancelled = true;
    };
  }, [formData.legs, selectedAircraftId, isCreating, operationalSleepOverActive]);

  useEffect(() => {
    if (!isCreating) return;
    if (sleepOverAssessment.autoEligible) return;
    if (!formData.sleepOverEnabled && formData.sleepOverOption !== 'NONE') {
      setFormData(prev => ({
        ...prev,
        sleepOverOption: 'NONE',
      }));
    }
  }, [formData.sleepOverEnabled, formData.sleepOverOption, isCreating, sleepOverAssessment.autoEligible]);

  useEffect(() => {
    if (!isCreating) return;
    const wasUnlocked = previousSleepOverUnlockedRef.current;
    previousSleepOverUnlockedRef.current = sleepOverUnlocked;
    if (!sleepOverUnlocked || wasUnlocked) return;
    if (formData.sleepOverOption !== 'NONE') return;
    setFormData(prev => {
      if ((prev.sleepOverEnabled || sleepOverAssessment.autoEligible) && prev.sleepOverOption === 'NONE') {
        return {
          ...prev,
          sleepOverOption: 'TIER1',
          sleepOverCustomAmount: prev.sleepOverOption === 'CUSTOM' ? prev.sleepOverCustomAmount : 0,
        };
      }
      return prev;
    });
  }, [formData.sleepOverOption, isCreating, sleepOverAssessment.autoEligible, sleepOverUnlocked]);

  const totals = useMemo(() => {
    const aircraft = selectedAircraft;
    if (!aircraft) {
      return {
        base: 0,
        landing: 0,
        handling: 0,
        nav: 0,
        taxes: 0,
        aircraftTax: 0,
        safetySecurity: 0,
        arrivalTaxes: 0,
        meetAndAssist: 0,
        lounge: 0,
        extraServices: 0,
        ancillaryLines: [],
        extraServiceLines: [],
        waiting: 0,
        total: 0,
        discountVal: 0,
        totalMinutes: 0,
        cost: 0,
        margin: 0,
      };
    }

    const legs = normalizeCharterLegsWithHomeBase(Array.isArray(formData.legs) ? formData.legs : [], { sleepOverActive: operationalSleepOverActive });

    const routeDefaults = getRouteDefaults();
    const globalSpeedKts = Number(routeDefaults.cruiseSpeedKts || 120);
    const globalRatePerHourRaw = Number(routeDefaults.ratePerHourUSD || 0);
    const globalRatePerMileRaw = Number(routeDefaults.ratePerNmUSD || 0);
    // Registry (Route Matrix) is the single base source of truth.
    // Per-route manual overrides are still applied later with higher priority.
    const globalRatePerHour = globalRatePerHourRaw > 0
      ? globalRatePerHourRaw
      : (globalRatePerMileRaw > 0 && globalSpeedKts > 0
          ? globalRatePerMileRaw * globalSpeedKts * KTS_TO_MPH
          : 0);
    const globalRatePerMile = globalRatePerMileRaw > 0
      ? globalRatePerMileRaw
      : (globalRatePerHour > 0 && globalSpeedKts > 0
          ? globalRatePerHour / (globalSpeedKts * KTS_TO_MPH)
          : 0);

    let totalBlockTime = 0;
    let baseCharge = 0;
    let operatingCost = 0;
    let totalHours = 0;
    const legMetrics = new Map<string, { distanceMiles: number; blockMinutes: number; baseCharge: number }>();

    legs.forEach(leg => {
      const metrics = getDerivedLegMetrics(leg, { preferManualDistance: true, preferManualBlock: true, deadLegFallback: 60 });
      if (!metrics) return;
      const { distanceMiles, blockMinutes } = metrics;

      const override = getDistanceOverride(leg.from, leg.to);
      const overrideRatePerMile = override?.overrideRatePerMileUSD || 0;
      const overrideRatePerHour = override?.overrideRatePerHourUSD || 0;
      const ratePerMile = overrideRatePerMile > 0 ? overrideRatePerMile : globalRatePerMile;
      const ratePerHour = overrideRatePerHour > 0 ? overrideRatePerHour : globalRatePerHour;

      const legHours = blockMinutes / 60;
      totalBlockTime += blockMinutes;
      totalHours += legHours;

      const useMileage = formData.pricingMode === 'MILEAGE' && distanceMiles > 0;
      const legBaseCharge = useMileage
        ? distanceMiles * ratePerMile
        : legHours * ratePerHour;
      baseCharge += legBaseCharge;
      legMetrics.set(leg.id, {
        distanceMiles,
        blockMinutes,
        baseCharge: legBaseCharge,
      });
    });

    if (formData.pricingMode === 'HOURS') {
      const minHours = aircraft.minBillableHours || 0;
      if (totalHours < minHours) {
        baseCharge += (minHours - totalHours) * globalRatePerHour;
      }
    }

    operatingCost = (totalBlockTime / 60) * aircraft.operatingCostPerHour;

    let landingFees = 0;
    let handlingFees = 0;
    let navFees = 0;
    let paxTaxes = 0;
    let aircraftTaxes = 0;
    let maFees = 0;
    let loungeFees = 0;
    let safetySecurityFees = 0;
    let totalPax = 0;
    const regulatoryByLeg: Array<{
      id: string;
      from: string;
      to: string;
      pax: number;
      departureTaxTotal: number;
      safetySecurityTotal: number;
    }> = [];
    const legBreakdowns: Array<{
      id: string;
      from: string;
      to: string;
      isDeadLeg: boolean;
      isImmigrationLeg: boolean;
      pax: number;
      distanceMiles: number;
      blockMinutes: number;
      baseCharge: number;
      landing: number;
      handling: number;
      navigation: number;
      departureTaxes: number;
      aircraftTax: number;
      aircraftTaxComponents: {
        landing: number;
        handling: number;
        navigation: number;
        paxBusLuggage: number;
        aircraftPermit: number;
        clearance: number;
        reposition: number;
      };
      safetySecurity: number;
      perPaxDepartureTax: number;
      perPaxSafetySecurity: number;
      ancillaries: number;
      extraServices: number;
      waitingTimeCharge: number;
      sleepOverCharge: number;
      total: number;
      isInternational: boolean;
    }> = [];
    let extraServicesTotal = 0;
    const extraServiceLines: Array<{
      legId: string;
      legNumber: number;
      from: string;
      to: string;
      code: string;
      name: string;
      scope: string;
      pricingModel: 'FIXED' | 'PER_PAX' | 'PER_HOUR';
      unitPrice: number;
      quantity: number;
      paxCount: number;
      blockMinutes: number;
      amount: number;
    }> = [];
    const ancillaryLines: Array<{
      legId: string;
      legNumber: number;
      from: string;
      to: string;
      name: string;
      scope: 'ORIGIN' | 'DESTINATION';
      unitPrice: number;
      paxCount: number;
      amount: number;
    }> = [];
    const waitingMinutes = Math.max(0, formData.waitingMinutes || 0);
    const waitFree = settings.waitFreeMinutes || 0;
    const waitRate = settings.waitRatePerHour || 0;
    const waitRounding = settings.waitRoundingMinutes || 1;
    const chargeableMinutes = Math.max(0, waitingMinutes - waitFree);
    const roundedMinutes = waitRounding > 0 ? Math.ceil(chargeableMinutes / waitRounding) * waitRounding : chargeableMinutes;
    const waitingFees = (roundedMinutes / 60) * waitRate;
    const sleepOverTier1 = Math.max(0, Number(settings.sleepOverPolicy?.tier1Price || 0));
    const sleepOverTier2 = Math.max(0, Number(settings.sleepOverPolicy?.tier2Price || 0));
    const sleepOverCustomSafe = Math.max(sleepOverTier1, Number(formData.sleepOverCustomAmount || 0));
    const sleepOverActive = Boolean(formData.sleepOverEnabled || sleepOverAssessment.autoEligible);
    const sleepOverCharge =
      !sleepOverActive
        ? 0
        : formData.sleepOverOption === 'TIER1'
        ? sleepOverTier1
        : formData.sleepOverOption === 'TIER2'
          ? sleepOverTier2
          : formData.sleepOverOption === 'CUSTOM'
            ? sleepOverCustomSafe
            : 0;
    const activeLegs = legs.filter(item => !item.isDeadLeg);
    const billingAnchorLegId = (activeLegs.length > 0 ? activeLegs[activeLegs.length - 1] : legs[legs.length - 1])?.id || '';

    legs.forEach((leg, legIndex) => {
      totalPax += leg.paxCount || 0;
      const commercial = sectorCommercialsByLeg[leg.id];
      const metrics = legMetrics.get(leg.id);
      const baseForLeg = metrics?.baseCharge || 0;
      let legLanding = 0;
      let legHandling = 0;
      let legNav = 0;
      let legTaxes = 0;
      let legAircraftTax = 0;
      let legSafety = 0;
      let legAncillaries = 0;
      let paxBusLuggage = 0;
      let aircraftPermit = 0;
      let clearance = 0;
      let reposition = 0;
      if (commercial?.breakdown) {
        legLanding = commercial.breakdown.portFees?.landing || 0;
        legHandling = commercial.breakdown.portFees?.handling || 0;
        legNav = commercial.breakdown.portFees?.navigation || 0;
        landingFees += legLanding;
        handlingFees += legHandling;
        navFees += legNav;
        legTaxes = commercial.breakdown.taxes || 0;
        paxTaxes += legTaxes;
        const fromAirstrip = findAirstripByAnyCode(leg.from);
        const toAirstrip = findAirstripByAnyCode(leg.to);
        if (fromAirstrip && toAirstrip) {
          paxBusLuggage = (leg.paxCount || 0) * (fromAirstrip.paxBusLuggageFee || 0);
          aircraftPermit = fromAirstrip.aircraftPermitFee || 0;
          clearance = fromAirstrip.clearanceFee || 0;
          reposition = fromAirstrip.repositionFee || 0;
        }
        legAircraftTax =
          legLanding +
          legHandling +
          legNav +
          paxBusLuggage +
          aircraftPermit +
          clearance +
          reposition;
        aircraftTaxes += legAircraftTax;
        legSafety = commercial.breakdown.safetySecurity || 0;
        safetySecurityFees += legSafety;
        regulatoryByLeg.push({
          id: leg.id,
          from: leg.from,
          to: leg.to,
          pax: leg.paxCount || 0,
          departureTaxTotal: legTaxes,
          safetySecurityTotal: legSafety,
        });
      } else {
        const fromAirstrip = findAirstripByAnyCode(leg.from);
        const airstrip = findAirstripByAnyCode(leg.to);
        if (airstrip && fromAirstrip) {
          legLanding = airstrip.landingFee || 0;
          legHandling = airstrip.handlingFee || 0;
          legNav = airstrip.navigationFee || 0;
          landingFees += legLanding;
          handlingFees += legHandling;
          navFees += legNav;
          const isDomestic = fromAirstrip?.country && airstrip.country && fromAirstrip.country === airstrip.country;
          const paxTax = isDomestic
            ? (fromAirstrip.paxTax || 0)
            : (fromAirstrip.paxTaxInternational || fromAirstrip.paxTax || 0);
          const legPax = leg.paxCount || 0;
          legTaxes = legPax * paxTax;
          paxTaxes += legTaxes;
          paxBusLuggage = (leg.paxCount || 0) * (fromAirstrip.paxBusLuggageFee || 0);
          aircraftPermit = fromAirstrip.aircraftPermitFee || 0;
          clearance = fromAirstrip.clearanceFee || 0;
          reposition = fromAirstrip.repositionFee || 0;
          legAircraftTax =
            legLanding +
            legHandling +
            legNav +
            paxBusLuggage +
            aircraftPermit +
            clearance +
            reposition;
          aircraftTaxes += legAircraftTax;
          const safetyFee = isDomestic
            ? (fromAirstrip.safetySecurityDomesticFee || 0)
            : (fromAirstrip.safetySecurityInternationalFee || 0);
          legSafety = legPax * safetyFee;
          safetySecurityFees += legSafety;
          regulatoryByLeg.push({
            id: leg.id,
            from: leg.from,
            to: leg.to,
            pax: legPax,
            departureTaxTotal: legTaxes,
            safetySecurityTotal: legSafety,
          });
        }
      }
      // Determine if leg is international (countries differ)
      // Use backend flag if available, otherwise calculate from airstrip countries
      let isInternational = false;
      if (commercial?.isInternational !== undefined) {
        isInternational = commercial.isInternational;
      } else {
        const fromAirstrip = findAirstripByAnyCode(leg.from);
        const toAirstrip = findAirstripByAnyCode(leg.to);
        isInternational = fromAirstrip && toAirstrip 
          ? (fromAirstrip.country !== toAirstrip.country)
          : false;
      }

      const fromAirstrip = findAirstripByAnyCode(leg.from);
      const toAirstrip = findAirstripByAnyCode(leg.to);
      const maScope = leg.meetAndAssistScope || 'DESTINATION';
      const loungeScope = leg.loungeScope || 'DESTINATION';
      const maAirstrip = maScope === 'ORIGIN' ? fromAirstrip : toAirstrip;
      const loungeAirstrip = loungeScope === 'ORIGIN' ? fromAirstrip : toAirstrip;

      if (leg.meetAndAssistActive) {
        const ma = (leg.paxCount || 0) * (maAirstrip?.meetAndAssistFee || 0);
        legAncillaries += ma;
        maFees += ma;
        ancillaryLines.push({
          legId: leg.id,
          legNumber: legIndex + 1,
          from: leg.from,
          to: leg.to,
          name: 'Meet & Assist',
          scope: maScope,
          unitPrice: maAirstrip?.meetAndAssistFee || 0,
          paxCount: leg.paxCount || 0,
          amount: Number(ma.toFixed(2)),
        });
      }

      if (leg.loungeActive) {
        const lounge = (leg.paxCount || 0) * (loungeAirstrip?.loungeFee || 0);
        legAncillaries += lounge;
        loungeFees += lounge;
        ancillaryLines.push({
          legId: leg.id,
          legNumber: legIndex + 1,
          from: leg.from,
          to: leg.to,
          name: 'VIP Lounge',
          scope: loungeScope,
          unitPrice: loungeAirstrip?.loungeFee || 0,
          paxCount: leg.paxCount || 0,
          amount: Number(lounge.toFixed(2)),
        });
      }

      const selectedExtraServices = (leg.extraServiceSelections || []) as QuoteLegExtraServiceSelection[];
      const extraServiceAmount = selectedExtraServices.reduce((sum, selection) => {
        const pricingLeg = {
          ...leg,
          blockTimeMinutes: metrics?.blockMinutes || leg.blockTimeMinutes || 0,
        } as Leg;
        const line = toCostLineFromSelection(selection, pricingLeg);
        extraServiceLines.push({
          legId: leg.id,
          legNumber: legIndex + 1,
          from: leg.from,
          to: leg.to,
          code: selection.code,
          name: selection.name,
          scope: selection.scope,
          pricingModel: selection.pricingModel,
          unitPrice: selection.unitPrice,
          quantity: selection.quantity || 1,
          paxCount: leg.paxCount || 0,
          blockMinutes: pricingLeg.blockTimeMinutes || 0,
          amount: line.amount,
        });
        return sum + line.amount;
      }, 0);
      extraServicesTotal += extraServiceAmount;

      const legTotal =
        baseForLeg +
        legTaxes +
        legAircraftTax +
        legSafety +
        legAncillaries +
        extraServiceAmount +
        (leg.id === billingAnchorLegId ? waitingFees : 0) +
        (leg.id === billingAnchorLegId ? sleepOverCharge : 0);
      const legPaxCount = leg.paxCount || 0;
      const hasImmigrationHandling =
        isImmigrationGeneratedLeg(leg) ||
        (Boolean(isInternational) && (Boolean(fromAirstrip?.isBorderPost) || Boolean(toAirstrip?.isBorderPost)));

      legBreakdowns.push({
        id: leg.id,
        from: leg.from,
        to: leg.to,
        isDeadLeg: Boolean(leg.isDeadLeg),
        isImmigrationLeg: hasImmigrationHandling,
        pax: legPaxCount,
        distanceMiles: metrics?.distanceMiles || 0,
        blockMinutes: metrics?.blockMinutes || 0,
        baseCharge: baseForLeg,
        landing: legLanding,
        handling: legHandling,
        navigation: legNav,
        departureTaxes: legTaxes,
        aircraftTax: legAircraftTax,
        aircraftTaxComponents: {
          landing: legLanding,
          handling: legHandling,
          navigation: legNav,
          paxBusLuggage,
          aircraftPermit,
          clearance,
          reposition,
        },
        safetySecurity: legSafety,
        perPaxDepartureTax: commercial?.breakdown?.perPaxDepartureTax ?? (legPaxCount > 0 ? legTaxes / legPaxCount : 0),
        perPaxSafetySecurity: commercial?.breakdown?.perPaxSafetySecurity ?? (legPaxCount > 0 ? legSafety / legPaxCount : 0),
        ancillaries: legAncillaries,
        extraServices: extraServiceAmount,
        waitingTimeCharge: leg.id === billingAnchorLegId ? waitingFees : 0,
        sleepOverCharge: leg.id === billingAnchorLegId ? sleepOverCharge : 0,
        total: legTotal,
        isInternational,
      });
    });

    const customer = customers.find(c => c.id === formData.customerId);
    const groupDiscount = customer?.categoryAssignments?.[0]?.Category?.defaultCharterDiscount || 0;
    const customerDiscount = customer?.charterDiscount || 0;
    const clientDiscountPercent = customerDiscount > 0 ? customerDiscount : groupDiscount;
    const issuerDiscountPercent = Number(formData.issuerDiscount || 0);
    const ceoDiscountPercent = Number(formData.ceoDiscount || 0);
    const totalDiscountPercent = clientDiscountPercent + issuerDiscountPercent + ceoDiscountPercent;
    const discountVal = (baseCharge * totalDiscountPercent) / 100;
    const clientDiscountVal = (baseCharge * clientDiscountPercent) / 100;
    const issuerDiscountVal = (baseCharge * issuerDiscountPercent) / 100;
    const ceoDiscountVal = (baseCharge * ceoDiscountPercent) / 100;
    const total =
      baseCharge +
      paxTaxes +
      aircraftTaxes +
      safetySecurityFees +
      maFees +
      loungeFees +
      extraServicesTotal +
      waitingFees +
      sleepOverCharge -
      discountVal;

    const perPaxDepartureTax = totalPax > 0 ? paxTaxes / totalPax : 0;
    const perPaxSafetySecurity = totalPax > 0 ? safetySecurityFees / totalPax : 0;

    return {
      base: baseCharge,
      landing: landingFees,
      handling: handlingFees,
      nav: navFees,
      taxes: paxTaxes,
      aircraftTax: aircraftTaxes,
      safetySecurity: safetySecurityFees,
      totalPax,
      perPaxDepartureTax,
      perPaxSafetySecurity,
      regulatoryByLeg,
      legBreakdowns,
      maFees,
      loungeFees,
      ancillaryLines,
      extraServices: extraServicesTotal,
      extraServiceLines,
      waiting: waitingFees + sleepOverCharge,
      waitingTimeCharge: waitingFees,
      sleepOverCharge,
      discountVal,
      discountBreakdown: {
        clientPercent: clientDiscountPercent,
        issuerPercent: issuerDiscountPercent,
        ceoPercent: ceoDiscountPercent,
        totalPercent: totalDiscountPercent,
        clientAmount: clientDiscountVal,
        issuerAmount: issuerDiscountVal,
        ceoAmount: ceoDiscountVal,
      },
      total,
      totalMinutes: totalBlockTime,
      cost: operatingCost,
      margin: total - operatingCost,
    };
  }, [formData, aircrafts, airstrips, airstripDistances, settings, sectorCommercialsByLeg, customers, sleepOverAssessment.autoEligible, operationalSleepOverActive]);

  const advisories = useMemo(() => {
    if (!settings.agent?.enabled || !settings.agent.modules?.QUOTER) return [];
    if (!settings.agent.rolesByModule?.QUOTER?.includes(currentRole)) return [];
    if (!isCreating) return [];
    const raw = getCharterAdvisories(formData, aircrafts, airstrips, settings, currentRole);
    const context = {
      customerId: formData.customerId,
      aircraftModel: formData.aircraftModel,
      date: formData.date,
      pricingMode: formData.pricingMode,
      waitingMinutes: formData.waitingMinutes,
      sleepOverEnabled: formData.sleepOverEnabled,
      sleepOverAutoEligible: sleepOverAssessment.autoEligible,
      sleepOverOption: formData.sleepOverOption,
      discount: formData.issuerDiscount + formData.ceoDiscount,
      legs: formData.legs,
      totals: { legsCount: Array.isArray(formData.legs) ? formData.legs.length : 0 },
    };
    const custom = evaluateCustomRules(settings.agent?.customRulesByModule?.QUOTER, context);
    const dynamic = (Array.isArray(formData.legs) ? formData.legs : []).flatMap((leg, idx) => {
      const legAdvisories: Array<{
        id: string;
        severity: 'LOW' | 'MEDIUM' | 'HIGH';
        title: string;
        detail: string;
        suggestion?: string;
        section: 'TIMING' | 'ROUTE';
        legId: string;
        legIndex: number;
      }> = [];
      const normalizedEtd = normalizeIso(String(leg.etd || ''), getMissionDateKey(leg));
      const normalizedEta = normalizeIso(String(leg.eta || ''), getMissionDateKey(leg));
      const derivedMetrics = getDerivedLegMetrics(leg, { preferManualDistance: true, preferManualBlock: false, deadLegFallback: 60 });
      const routeBlockMinutes = Math.max(0, Number(derivedMetrics?.blockMinutes || 0));
      const enteredBlockMinutes = Math.max(0, Number(leg.blockTimeMinutes || 0));
      if (normalizedEtd && normalizedEta) {
        const etdMs = new Date(normalizedEtd).getTime();
        const etaMs = new Date(normalizedEta).getTime();
        const etaDeltaMinutes = Number.isFinite(etdMs) && Number.isFinite(etaMs)
          ? Math.round((etaMs - etdMs) / 60000)
          : 0;
        if (etaDeltaMinutes <= 0) {
          legAdvisories.push({
            id: `charter.leg.${idx + 1}.eta_before_etd`,
            severity: 'HIGH',
            title: `Leg ${idx + 1} ETA is before ETD`,
            detail: 'Arrival time must be later than departure time.',
            suggestion: 'Adjust ETA or ETD so the sector duration is positive.',
            section: 'TIMING',
            legId: leg.id,
            legIndex: idx,
          });
        } else if (enteredBlockMinutes > 0 && Math.abs(etaDeltaMinutes - enteredBlockMinutes) >= 5) {
          const diff = etaDeltaMinutes - enteredBlockMinutes;
          legAdvisories.push({
            id: `charter.leg.${idx + 1}.eta_block_mismatch`,
            severity: 'MEDIUM',
            title: `Leg ${idx + 1} ETA does not match block time`,
            detail: `ETD to ETA gives ${etaDeltaMinutes} min, but block time is ${enteredBlockMinutes} min (${diff > 0 ? '+' : ''}${diff} min difference).`,
            suggestion: 'Align ETA with block time, or update the block time to match the schedule.',
            section: 'TIMING',
            legId: leg.id,
            legIndex: idx,
          });
        }
      }
      if (enteredBlockMinutes > 0 && routeBlockMinutes > 0 && Math.abs(enteredBlockMinutes - routeBlockMinutes) >= 10) {
        const drift = enteredBlockMinutes - routeBlockMinutes;
        legAdvisories.push({
          id: `charter.leg.${idx + 1}.block_route_mismatch`,
          severity: 'LOW',
          title: `Leg ${idx + 1} block time differs from route matrix`,
          detail: `Entered block time is ${enteredBlockMinutes} min, while route matrix suggests ${routeBlockMinutes} min (${drift > 0 ? '+' : ''}${drift} min).`,
          suggestion: 'Keep manual block time only if operations intentionally overrode the route timing.',
          section: 'TIMING',
          legId: leg.id,
          legIndex: idx,
        });
      }
      if (!leg.isDeadLeg && normalizedEtd && canonicalizeAirstripCode(leg.from) !== getBaseAirstrip()) {
        const prePositionMetrics = getDerivedLegMetrics({
          from: getBaseAirstrip(),
          to: leg.from,
          isDeadLeg: true,
        }, { preferManualDistance: false, preferManualBlock: false, deadLegFallback: 60 });
        const prePositionBlockMinutes = Math.max(0, Number(prePositionMetrics?.blockMinutes || 0));
        if (prePositionBlockMinutes > 0) {
          const prePositionDepartureIso = addMinutesToIso(normalizedEtd, -prePositionBlockMinutes);
          const departureParts = getBusinessDateTimeParts(prePositionDepartureIso);
          if (departureParts) {
            legAdvisories.push({
              id: `charter.leg.${idx + 1}.preposition_required`,
              severity: 'LOW',
              title: `Leg ${idx + 1} requires Arusha pre-positioning`,
              detail: `To operate ${canonicalizeAirstripCode(leg.from)} on time, aircraft must depart ${getBaseAirstrip()} at ${departureParts.timeLabel} Tanzania time.`,
              suggestion: 'Review ETD and confirm the positioning leg timing is operationally realistic.',
              section: 'TIMING',
              legId: leg.id,
              legIndex: idx,
            });
          }
        }
      }
      return legAdvisories;
    });
    const overnightTimingAdvisories = (() => {
      const normalizedLegs = normalizeCharterLegsWithHomeBase(Array.isArray(formData.legs) ? formData.legs : [], { sleepOverActive: false });
      const missionMap = new Map<string, Leg[]>();
      normalizedLegs.forEach((leg) => {
        const key = getMissionDateKey(leg);
        const current = missionMap.get(key) || [];
        current.push(leg);
        missionMap.set(key, current);
      });
      const cutoffMinutes = (18 * 60) + 30;
      return Array.from(missionMap.values()).flatMap((missionLegs, missionIndex) => {
        const lastLeg = missionLegs[missionLegs.length - 1];
        if (!lastLeg || canonicalizeAirstripCode(lastLeg.to) !== getBaseAirstrip()) return [];
        const activeLegs = missionLegs.filter(item => !item.isDeadLeg);
        const billingLeg = activeLegs[activeLegs.length - 1] || lastLeg;
        const etaIso = getLegEndIso(lastLeg, Number(lastLeg.blockTimeMinutes || 0));
        const etaParts = getBusinessDateTimeParts(etaIso);
        if (!etaParts) return [];
        const etaMinutes = (etaParts.hour * 60) + etaParts.minute;
        const qualifies = etaMinutes >= cutoffMinutes;
        if (!qualifies) return [];
        return [{
          id: `charter.mission.${missionIndex + 1}.overnight_cutoff`,
          severity: 'MEDIUM' as const,
          title: 'Mission timing reaches overnight cutoff',
          detail: `Return to ${getBaseAirstrip()} reaches ${etaParts.timeLabel} Tanzania time, beyond the 18:30 cutoff.`,
          suggestion: 'Confirm overnight handling or move timings earlier if same-day return is required.',
          section: 'TIMING' as const,
          legId: billingLeg.id,
          legIndex: (Array.isArray(formData.legs) ? formData.legs : []).findIndex(item => item.id === billingLeg.id),
        }];
      });
    })();
    return filterAdvisories([...raw, ...custom, ...dynamic, ...overnightTimingAdvisories], settings, 'QUOTER');
  }, [formData, aircrafts, airstrips, settings, currentRole, isCreating, sleepOverAssessment.autoEligible, airstripDistances]);

  const enforcement = getEnforcementPolicy(settings, 'QUOTER');
  const shouldBlock = !settings.agent?.advisoryOnly && advisories.some(a => enforcement.blockSeverities.includes(a.severity));

  const acknowledgeAdvisories = async () => {
    if (!advisories.length) return;
    await agentService.logAudit('AGENT_ADVISORY_ACK', {
      module: 'QUOTER',
      count: advisories.length,
      advisories,
    });
  };

  const saveQuote = async () => {
    if (!hasValidReturnToBase(normalizeCharterLegsWithHomeBase(formData.legs || [], { sleepOverActive: operationalSleepOverActive }))) {
      alert(`Return leg to base ${getBaseAirstrip()} is required before authorizing this quote.`);
      return;
    }
    if (settings.agent?.enabled && settings.agent.modules?.QUOTER) {
      const finalAuditSeverities = (settings.agent.enforcementByModule?.QUOTER || settings.agent.enforcement).blockSeverities || ['HIGH'];
      const finalAuditFail = advisories.some(a => finalAuditSeverities.includes(a.severity));
      if (finalAuditFail) {
        alert('Final Audit failed: resolve blocking advisories before sending.');
        agentService.logAudit('AGENT_FINAL_AUDIT_FAIL', { module: 'QUOTER', advisories });
        return;
      }
    }
    if (shouldBlock) {
      const overrideRoles = enforcement.overrideRoles || ['ADMIN', 'DFO'];
      if (overrideRoles.length > 0 && !overrideRoles.includes(currentRole)) {
        alert('Override denied: your role is not authorized to approve overrides.');
        agentService.logAudit('AGENT_OVERRIDE_DENIED', { module: 'QUOTER', advisories, role: currentRole });
        return;
      }
      if (enforcement.allowOverride) {
        const reason = enforcement.requireOverrideReason
          ? prompt('Override reason required to proceed:')
          : prompt('Confirm override? (Optional reason)');
        if (enforcement.requireOverrideReason && (!reason || reason.trim() === '')) {
          alert('Override cancelled. Reason is required.');
          return;
        }
        agentService.logAudit('AGENT_OVERRIDE', { module: 'QUOTER', advisories, reason: reason || '' });
      } else {
        alert('Blocked by Supervisor Agent: resolve required advisories first.');
        agentService.logAudit('AGENT_BLOCK', { module: 'QUOTER', advisories });
        return;
      }
    }
    const customer = customers.find(c => c.id === formData.customerId);
    const aircraft = selectedAircraft;
    const status: Quote['status'] = 'DRAFT';

    const addMinutesToTime = (dateStr: string, timeStr: string, minutes: number) => {
      const base = new Date(`${dateStr}T${timeStr}`);
      if (Number.isNaN(base.getTime())) return null;
      const next = new Date(base.getTime() + minutes * 60000);
      return next.toISOString();
    };

    const addMinutesToIso = (iso: string, minutes: number) => {
      const base = new Date(iso);
      if (Number.isNaN(base.getTime())) return iso;
      return new Date(base.getTime() + minutes * 60000).toISOString();
    };

    const toIso = (value?: string) => {
      if (!value) return null;
      if (value.includes('T')) {
        const parsed = new Date(value);
        return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
      }
      if (/^\d{2}:\d{2}$/.test(value)) {
        return addMinutesToTime(formData.date, value, 0);
      }
      return null;
    };
    const inferLegBlockMinutes = (leg: Leg, etdIso: string, etaIso?: string | null) => {
      const explicit = Number(leg.blockTimeMinutes || 0);
      if (explicit > 0) return explicit;
      const fromEtd = new Date(etdIso).getTime();
      const fromEta = etaIso ? new Date(etaIso).getTime() : NaN;
      if (Number.isFinite(fromEtd) && Number.isFinite(fromEta) && fromEta > fromEtd) {
        return Math.max(1, Math.round((fromEta - fromEtd) / 60000));
      }
      const breakdown = (totals.legBreakdowns || []).find((item: any) => String(item.id || '') === String(leg.id || ''));
      const breakdownMinutes = Number((breakdown as any)?.blockMinutes || 0);
      if (breakdownMinutes > 0) return breakdownMinutes;
      return leg.isDeadLeg ? 60 : 55;
    };

    const normalizedFormLegs = normalizeCharterLegsWithHomeBase(formData.legs || [], { sleepOverActive: operationalSleepOverActive });
    const normalizeLegs = normalizedFormLegs.map((leg, index) => {
      const etdIso = toIso((leg as any).etd) || new Date(formData.date).toISOString();
      const parsedEtaIso = toIso((leg as any).eta);
      const blockMinutes = inferLegBlockMinutes(leg, etdIso, parsedEtaIso);
      const etaIso =
        parsedEtaIso && new Date(parsedEtaIso).getTime() > new Date(etdIso).getTime()
          ? parsedEtaIso
          : addMinutesToIso(etdIso, blockMinutes);
      return {
        ...leg,
        from: canonicalizeAirstripCode(leg.from),
        to: canonicalizeAirstripCode(leg.to),
        departureICAO: canonicalizeAirstripCode((leg as any).departureICAO || leg.from),
        arrivalICAO: canonicalizeAirstripCode((leg as any).arrivalICAO || leg.to),
        blockTimeMinutes: blockMinutes,
        etd: etdIso,
        eta: etaIso,
        legNumber: index + 1,
      };
    });

    const payload: Partial<Quote> = {
      customerId: formData.customerId,
      customerName: customer?.name || 'Unknown',
      date: formData.date,
      productMode: 'CHARTER',
      aircraftModel: formData.aircraftModel,
      aircraftId: undefined,
      tailNumber: undefined,
      legs: normalizeLegs,
      totalBlockTimeMinutes: totals.totalMinutes,
      billableTimeMinutes: Math.max(totals.totalMinutes, (aircraft?.minBillableHours || 0) * 60),
      discountPercentage: totals.discountBreakdown.totalPercent,
      costs: {
        pricingMode: formData.pricingMode,
        base: totals.base,
        landingFees: totals.landing,
        handlingFees: totals.handling,
        navigationFees: totals.nav,
        taxes: totals.taxes,
        aircraftTax: totals.aircraftTax,
        safetySecurity: totals.safetySecurity,
        meetAndAssist: totals.maFees,
        lounge: totals.loungeFees,
        ancillaryLines: totals.ancillaryLines,
        extraServices: totals.extraServices,
        extraServiceLines: totals.extraServiceLines,
        waiting: totals.waiting,
        waitingTimeCharge: totals.waitingTimeCharge,
        sleepOverEnabled: Boolean(formData.sleepOverEnabled),
        sleepOverCharge: totals.sleepOverCharge,
        sleepOverOption: formData.sleepOverOption,
        sleepOverCustomAmount: formData.sleepOverOption === 'CUSTOM' ? Math.max(Number(settings.sleepOverPolicy?.tier1Price || 0), Number(formData.sleepOverCustomAmount || 0)) : 0,
        discount: totals.discountVal,
        discountBreakdown: totals.discountBreakdown,
      },
      totalUSD: totals.total,
      pnl: { margin: totals.margin, cost: totals.operatingCost },
      status: status,
      requiredApprovals: formData.issuerDiscount > charterDiscountLimit ? ['DFO'] : [],
    };
    try {
      if (editingQuoteId) {
        const updated = await quoteApi.updateQuote(editingQuoteId, payload);
        const updatedQuote = (updated as any)?.data || updated;
        setQuotes(prev => prev.map(q => (q.id === updatedQuote?.id ? updatedQuote : q)));
        setIsCreating(false);
        setEditingQuoteId(null);
        onLog?.('QUOTER', 'UPDATE', `Charter ${updatedQuote?.id || 'quote'} updated. Status: ${status}`, 'LOW');
        return;
      }

      const created = await quoteApi.createQuote(payload);
      const createdQuote = (created as any)?.data || created;
      setQuotes(prev => [createdQuote, ...prev]);
      setFormData(prev => ({
        ...prev,
        bookingId: createdQuote?.bookingId || prev.bookingId || '',
        pnrCode: createdQuote?.pnrCode || prev.pnrCode || '',
      }));
      setIsCreating(false);
      onLog?.('QUOTER', 'CREATE', `Charter ${createdQuote?.id || 'quote'} created. Status: ${status}`, 'LOW');
    } catch (error: any) {
      console.error('Failed to create/update quote:', error);
      const backendMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to save quote. Please try again.';
      alert(backendMessage);
    }
  };

  const sendQuote = async (
    quoteId: string,
    options: {
      includeBreakdown?: boolean;
      sendMode?: 'NEW' | 'SAME_THREAD';
      accountId?: string;
      htmlTemplateId?: string;
      pdfTemplateId?: string;
      to?: string;
      cc?: string;
      bcc?: string;
      threadMessageId?: string;
    } = {}
  ) => {
    try {
      const updatedQuote = await quoteApi.sendQuote(quoteId, options);
      setQuotes(prev => prev.map(q => (q.id === updatedQuote?.id ? updatedQuote : q)));
      onLog?.('QUOTER', 'SEND', `Charter ${updatedQuote?.id || quoteId} sent to client`, 'LOW');
    } catch (error: any) {
      console.error('Failed to send quote:', error);
      const backendMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to send quote. Please try again.';
      alert(backendMessage);
    }
  };

  const refreshZohoEstimateStatus = async (quoteId: string) => {
    try {
      const updatedQuote = await quoteApi.refreshZohoEstimateStatus(quoteId);
      const normalized = (updatedQuote as any)?.data || updatedQuote;
      setQuotes(prev => prev.map(q => (q.id === normalized?.id ? normalized : q)));
      onLog?.('QUOTER', 'SYNC', `Zoho estimate status refreshed for ${quoteId}`, 'LOW');
      return normalized;
    } catch (error) {
      console.error('Failed to refresh Zoho estimate status:', error);
      alert('Failed to refresh Zoho estimate status. Please try again.');
      return null;
    }
  };

  return {
    isCreating,
    setIsCreating,
    formData,
    setFormData,
    discountSource,
    handleIssuerDiscountChange,
    handleCeoDiscountChange,
    resetDiscountSource,
    startNewQuote,
    addLeg,
    autoReturnToBase,
    removeLeg,
    updateLeg,
    resolvedExtraServicesByLeg,
    weatherByCode,
    totals,
    advisories,
    canAuthorizeQuote: hasValidReturnToBase(normalizeCharterLegsWithHomeBase(formData.legs || [], { sleepOverActive: operationalSleepOverActive })),
    authorizeBlockReason: hasValidReturnToBase(normalizeCharterLegsWithHomeBase(formData.legs || [], { sleepOverActive: operationalSleepOverActive }))
      ? ''
      : `Add return leg to base ${getBaseAirstrip()} before authorizing.`,
    shouldBlock,
    acknowledgeAdvisories,
    saveQuote,
    selectQuote,
    sendQuote,
    refreshZohoEstimateStatus,
    sleepOverAssessment,
    clearEditing: () => setEditingQuoteId(null),
    quotes,
  };
};
